import React, { useState } from 'react'
import {
    Button,
    Menu,
    MenuItem,
    ListSubheader,
    Typography,
    Box,
    CircularProgress,
} from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import {
    useMergeVariables,
    MergeVariable,
} from '../../../../hooks/Outreach/useCampaign'

const useStyles = makeStyles((theme: Theme) => ({
    insertButton: {
        padding: '4px 8px',
        fontSize: '0.75rem',
        textTransform: 'none',
        color: '#6b7280',
        borderColor: '#d1d5db',
        borderRadius: 4,
        minWidth: 'auto',
        '&:hover': {
            backgroundColor: '#f1f5f9',
            borderColor: '#3b82f6',
            color: '#3b82f6',
        },
    },
    subheader: {
        fontWeight: 600,
        fontSize: '0.7rem',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: '#9ca3af',
        lineHeight: '32px',
        backgroundColor: '#ffffff',
    },
    menuItem: {
        fontSize: '0.8rem',
        padding: theme.spacing(0.75, 2),
        '&:hover': {
            backgroundColor: '#eff6ff',
        },
    },
    variableKey: {
        fontFamily: 'monospace',
        fontSize: '0.7rem',
        color: '#6b7280',
        marginLeft: theme.spacing(1),
    },
}))

const CATEGORY_LABELS: Record<string, string> = {
    prospect: 'Prospect',
    property: 'Property',
    campaign: 'Campaign',
    concession: 'Concession',
}

/** Excluded from picker only; placeholders still resolve if typed manually */
const MERGE_KEYS_HIDDEN_FROM_DROPDOWN = new Set([
    'prospect_company',
    'prospect_job_title',
])

interface MergeVariableDropdownProps {
    onSelect: (variableKey: string) => void
}

export const MergeVariableDropdown: React.FC<MergeVariableDropdownProps> = ({
    onSelect,
}) => {
    const classes = useStyles()
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const { mergeVariables, isLoadingMergeVariables } = useMergeVariables()

    const insertableVariables = mergeVariables.filter(
        (v) => !MERGE_KEYS_HIDDEN_FROM_DROPDOWN.has(v.key),
    )

    const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault()
        e.stopPropagation()
        setAnchorEl(e.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    const handleSelect = (variable: MergeVariable) => {
        onSelect(`{{${variable.key}}}`)
        handleClose()
    }

    const grouped = insertableVariables.reduce<Record<string, MergeVariable[]>>(
        (acc, variable) => {
            const cat = variable.category
            if (!acc[cat]) acc[cat] = []
            acc[cat].push(variable)
            return acc
        },
        {},
    )

    return (
        <>
            <Button
                variant="outlined"
                size="small"
                onMouseDown={handleOpen}
                className={classes.insertButton}
            >
                {'{{ }}'} Insert Variable
            </Button>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                PaperProps={{
                    style: {
                        maxHeight: 320,
                        minWidth: 240,
                    },
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                getContentAnchorEl={null}
            >
                {isLoadingMergeVariables ? (
                    <Box display="flex" justifyContent="center" p={2}>
                        <CircularProgress size={20} />
                    </Box>
                ) : insertableVariables.length === 0 ? (
                    <MenuItem disabled>
                        <Typography variant="body2" color="textSecondary">
                            No variables available
                        </Typography>
                    </MenuItem>
                ) : (
                    Object.entries(grouped).map(([category, variables]) => [
                        <ListSubheader
                            key={`header-${category}`}
                            className={classes.subheader}
                        >
                            {CATEGORY_LABELS[category] || category}
                        </ListSubheader>,
                        ...variables.map((variable) => (
                            <MenuItem
                                key={variable.key}
                                onClick={() => handleSelect(variable)}
                                className={classes.menuItem}
                            >
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    width="100%"
                                >
                                    <Typography variant="body2">
                                        {variable.label}
                                    </Typography>
                                    <Typography className={classes.variableKey}>
                                        {`{{${variable.key}}}`}
                                    </Typography>
                                </Box>
                            </MenuItem>
                        )),
                    ])
                )}
            </Menu>
        </>
    )
}

export default MergeVariableDropdown
